import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, removeToken } from '@xoxokiosk/shared';
import { useAuth } from '../contexts/AuthContext';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, Alert, AlertDescription } from '@xoxokiosk/ui';
import { AlertCircle, Loader2 } from 'lucide-react';

// Define the expected response type for better type safety
interface LoginResponse {
  user: {
    id: string;
    username: string;
    // Add other user fields as needed
  };
  shop: any; // Replace 'any' with proper Shop type when available
  machine: any; // Replace 'any' with proper Machine type when available
  tokens: {
    access: { token: string; expires: string };
    refresh: { token: string; expires: string };
  };
}

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const { setAuthFromLogin } = useAuth();

  // Clear any existing tokens on component mount
  useEffect(() => {
    removeToken();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Basic client-side validation
    if (!username.trim() || !password) {
      setError('Please enter both username and password');
      return;
    }

    setIsLoading(true);
    try {
      const res = await authApi.login({ username, password });
      const { data } = res as { data: LoginResponse };
      
      if (data?.tokens?.access?.token && data?.tokens?.refresh?.token) {
  // Pass login data into auth context which will persist tokens and user
  setAuthFromLogin(data);

  // Decide destination: admins and shop-owners both land on shop admin page
  const role = (data.user as any)?.role || null;
  const shop = data.shop || null;

  // Normalize role to uppercase to handle different casings from the backend
  const roleNormalized = String(role || '').toUpperCase();

  if (roleNormalized === 'ADMIN' || (data.user as any)?.isAdmin) {
    navigate('/shop-admin');
  } else if (roleNormalized === 'SHOP_OWNER' || shop) {
    const id = shop?.id || shop?.shop_id || shop?._id;
    if (id) navigate(`/shops/${id}`);
    else navigate('/shop-admin'); // fallback to shop admin if shop id missing
  } else {
    // Fallback: if no shop or admin role, go to landing
    navigate('/landing');
  }
      } else {
        console.error('Invalid response format from server - missing tokens', data);
        throw new Error('Invalid response format from server - missing tokens');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      let errorMessage = 'Login failed. Please try again.';
      
      if (err.response) {
        // Server responded with an error status (4xx, 5xx)
        errorMessage = err.response.data?.message || `Server error: ${err.response.status}`;
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        // Something else happened
        errorMessage = err.message || 'An unexpected error occurred';
      }
      
      setError(errorMessage);
      
      // Clear password field on error for security
      setPassword('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/company-logo.svg" 
              alt="XOXO Beverages" 
              className="h-16 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold">XOXO Kiosk Admin</CardTitle>
          <CardDescription>Here's to another drink!</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={isLoading}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                required
              />
            </div>
            
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;

